import { useEffect } from "react";
import FXBackdrop from "../components/FXBackdrop";
import PageTransition from "../components/PageTransition";
import { motion } from "framer-motion";
import { Users } from "lucide-react";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.55, ease: "easeOut", delay },
});

const founders = [
  {
    name: "Muhammad Saif",
    role: "CEO & Co-Founder",
    photo: "/assets/founders/Saif.jpeg",
    story: [
      "Saif studies Astrophysics and History at Colby College, splitting time between Colby and Oxford, training that sharpened his eye for structural inefficiencies. While at Oxford, he watched the same story play out around him: founders he crossed paths with spending months on recruiting while overlooking developer talent in South Asia entirely. He founded Hirrd in October 2025 to close that gap, while concurrently co-founding Agrobyte since January 2026. His operating chops are real and hands-on, from driving strategy through market analysis as a Business Growth Intern at Afzal Electronics to running full-scale logistics as Colby's Orientation Coordinator. As Founder, he holds Hirrd to the same bar: every feature and screening decision has to make geography irrelevant to great hiring.",
    ],
  },
  {
    name: "Nofal Nadeem",
    role: "COO & Co-Founder",
    photo: "/assets/founders/Nofal.jpg",
    story: [
      "Nofal is a Computer Science graduate from FAST NUCES who has spent years mastering the full stack of modern development, from MERN to DevOps, cloud infrastructure, and MLOps orchestration. When Saif laid out the problem in October 2025, Nofal immediately saw how to build the solution at scale. As COO, he owns Hirrd's operational backbone, architecting the systems that screen hundreds of applications, match developers to opportunities, and deliver pre-vetted candidates within 24 hours. He does not just ask whether something can be built, he asks whether it can scale and hold up under pressure, which is why Hirrd's infrastructure runs smooth and reliable instead of chaotic.",
    ],
  },
  {
    name: "Asher Khan",
    role: "CTO & Co-Founder",
    photo: "/assets/founders/Asher.jpeg",
    story: [
      "Asher is an Artificial Intelligence graduate from FAST NUCES who was doing production level machine learning work while still in school. As CTO since January 2026, he engineered the screening algorithms that evaluate developer quality, the matching engines that connect talent with opportunities, and the automation that turns hundreds of applications into curated candidate lists. What sets him apart is a commitment to transparent systems: he understands algorithmic fairness and builds screening that developers can trust because it is grounded in solid technical foundations, engineered to perform reliably and scale as Hirrd grows.",
    ],
  },
];

const associates = [
  {
    name: "Aiemah Afzal",
    role: "Founder's Associate",
    initials: "AA",
    story: [
      "Aiemah is a Business Analytics graduate from FAST NUCES and has served as Associate Founder since February 2026, helping curate strategy at Hirrd. While the rest of the team works off instinct about what is working, she asks the sharper questions: which sourcing channels produce the best candidates, what the real conversion rate through the screening funnel looks like, how long onboarding actually takes, and which developers see the most placement success. That methodological rigor is what keeps Hirrd from just throwing money at hiring and hoping for the best. She spots patterns and inefficiencies before they are obvious, making sure Hirrd scales with data guiding every decision rather than guesswork.",
    ],
  },
  {
    name: "Aqsa Siddique",
    role: "Founder's Associate",
    initials: "AS",
    story: [
      "Aqsa is a Business Analytics graduate from FAST NUCES, starting her MSBA at Boston University this fall, and she moves fluently between data and business. She writes SQL, builds dashboards, and understands machine learning, but she also thinks commercially, asking which metrics actually matter and which features are worth building. Her real strength is translation: she sits with developers to understand their technical concerns, explains to clients why the screening process works, and pressure tests proposals with hard questions about ROI. That ability to bridge technical teams, business goals, and customer needs is exactly what a young company like Hirrd needs as it scales.",
    ],
  },
];

function PhotoCard({ person, index, priority = false }) {
  const motionProps = priority
    ? { initial: { opacity: 1, y: 0 }, animate: { opacity: 1, y: 0 } }
    : fadeUp(index * 0.12);

  return (
    <motion.div
      {...motionProps}
      className="glass p-8 md:p-10 grid lg:grid-cols-[300px_1fr] gap-8 items-start"
    >
      <div className="w-full max-w-[300px] mx-auto lg:mx-0">
        <div className="w-full rounded-2xl overflow-hidden" style={{ aspectRatio: "4/5" }}>
          <img
            src={person.photo}
            alt={person.name}
            className="w-full h-full object-cover object-top"
            loading={priority ? "eager" : "lazy"}
            decoding={priority ? "sync" : "async"}
            fetchPriority={priority ? "high" : "auto"}
          />
        </div>
      </div>

      <div className="flex flex-col justify-start">
        <div className="chip w-fit">{person.role}</div>
        <h2 className="h2 mt-4">{person.name}</h2>
        <div className="mt-5 grid gap-4">
          {person.story.map((para, i) => (
            <p key={i} className="p leading-relaxed">{para}</p>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function AssociateCard({ person, index }) {
  return (
    <motion.div
      {...fadeUp(index * 0.1)}
      className="glass-dark p-7 flex flex-col gap-5"
    >
      <div className="flex items-center gap-4">
        <div
          className="h-14 w-14 rounded-2xl flex items-center justify-center text-lg font-semibold shrink-0"
          style={{ background: "rgba(139,92,246,0.18)", border: "1px solid rgba(139,92,246,0.3)", color: "rgba(196,181,253,1)" }}
        >
          {person.initials}
        </div>
        <div>
          <div className="font-semibold text-lg">{person.name}</div>
          <div className="chip mt-1 w-fit text-xs">{person.role}</div>
        </div>
      </div>
      <div className="grid gap-3">
        {person.story.map((para, i) => (
          <p key={i} className="p text-sm leading-relaxed">{para}</p>
        ))}
      </div>
    </motion.div>
  );
}

export default function Team() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = "/assets/founders/Saif.jpeg";
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  return (
    <PageTransition>
      <FXBackdrop>

        {/* ── HERO ── */}
        <section className="containerX pt-14 md:pt-20 pb-10 relative">
          <div className="float-shape bg-violet-400 h-14 w-14 top-6 right-16 hidden lg:block" style={{ animationDelay: "0s" }} />
          <div className="float-shape-slow bg-purple-300 h-9 w-9 bottom-4 left-8 hidden lg:block" style={{ animationDelay: "2s" }} />

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <div className="chip"><Users size={14} /> Our Team</div>
            <h1 className="h1 mt-5">
              The people who make{" "}
              <span className="text-black">hirrd run.</span>
            </h1>
            <p className="p mt-5 text-lg max-w-2xl">
              A small, focused team built from people who understand both sides of the hiring problem. They refused to accept that geography should be the reason brilliant people and ambitious companies never meet.
            </p>
          </motion.div>
        </section>

        {/* ── CO-FOUNDERS ── light */}
        <section className="containerX pb-16 relative">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-6 pl-1">
            Co-Founders
          </div>
          <div className="grid gap-6">
            {founders.map((person, i) => (
              <PhotoCard key={person.name} person={person} index={i} priority={i === 0} />
            ))}
          </div>
        </section>

        {/* ── ASSOCIATES ── dark */}
        <section className="dark-section py-16 relative overflow-hidden">
          <div className="float-shape bg-purple-500 h-16 w-16 bottom-8 right-8" style={{ animationDelay: "0.5s" }} />
          <div className="float-shape-slow bg-violet-500 h-24 w-24 -top-8 -left-8" style={{ animationDelay: "1s" }} />
          <div className="containerX">
            <div className="text-xs font-semibold uppercase tracking-widest mb-6 pl-1" style={{ color: "rgba(139,92,246,0.7)" }}>
              Founders' Associates
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {associates.map((person, i) => (
                <AssociateCard key={person.name} person={person} index={i} />
              ))}
            </div>
          </div>
        </section>

      </FXBackdrop>
    </PageTransition>
  );
}
